import {
  Avatar,
  Box,
  Container,
  Grid,
  IconButton,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DetailContainer } from 'components/terms/Containers';
import { useTitle } from 'utils/router';

import HeroSection from 'components/info/Hero';
import personIm from 'assets/images/lps/personimage5.webp';
import SaleemIm from 'assets/images/user/Saleem.png';
import JonathanIm from 'assets/images/user/Jonathan.png';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Link } from 'react-router-dom';

const StyledIconButton = styled(IconButton)`
  & .MuiSvgIcon-root {
    fill: ${({ theme }) => theme.palette.grey[700]};
    &:hover {
      opacity: 0.8;
    }
  }
`;

export default function About(): JSX.Element {
  useTitle('About Us');
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      <HeroSection
        title={'About us'}
        subtext="Freelancing should mean freedom—not endless searching."
        minHeight={isMobileScreen ? '260px' : '470px'}
        personImage={personIm}
      />
      <Box bgcolor="#F8F7F4">
        <Container maxWidth="lg">
          <DetailContainer>
            <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
              You’re a freelancer because you want to spend your time working on projects you love, not endlessly
              scrolling through gig listings or competing with hundreds of others for a decent job.
            </Typography>
            <Typography variant={isMobileScreen ? 'body2' : 'body1'}>We get it—because we’ve been there.</Typography>
            <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
              That’s why we built Shoutt, the ultimate gig aggregator designed specifically for freelancers who value
              their time, energy, and peace of mind. We’re not another job board; we’re your personal shortcut to better
              freelance opportunities.
            </Typography>
            <ul style={{ margin: '0px', marginTop: '8px' }}>
              <li>
                <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
                  <b>All the best gigs in one place: </b> We gather over 1,500 fresh gigs each month from more than 40
                  trusted freelance platforms. No more jumping from site to site—just one streamlined hub.
                </Typography>
              </li>
              <li style={{ marginTop: '8px' }}>
                <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
                  <b>Fresh gigs, every day:</b> Every gig you see on Shoutt is less than a week old. Pitch early, stand
                  out, and land more projects with less effort.
                </Typography>
              </li>
              <li style={{ marginTop: '8px' }}>
                <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
                  <b>Quality over quantity: </b> Say goodbye to vague briefs, outdated listings, and gigs that
                  undervalue your skills. We carefully filter out low-quality leads, presenting only meaningful
                  opportunities worth your time.
                </Typography>
              </li>
              <li style={{ marginTop: '8px' }}>
                <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
                  <b>Personalised for your success: </b> With powerful AI-driven personalisation tools coming soon (and
                  even more gigs), Shoutt will get smarter about understanding exactly what you’re looking for. That
                  means fewer irrelevant gigs and more projects tailored specifically to your expertise and goals.
                </Typography>
              </li>
              <li style={{ marginTop: '8px' }}>
                <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
                  <b>Smart growth guidance: </b> We’re also building intelligent tools to guide you toward smarter,
                  strategic decisions for your freelance career. From identifying new opportunities to helping you grow
                  your business sustainably, Shoutt is more than a gig aggregator—it’s your growth partner.
                </Typography>
              </li>
            </ul>
            <Typography variant={isMobileScreen ? 'h6' : 'h5'} mt={1.5}>
              Our Mission
            </Typography>
            <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
              Freelancing is about doing great work and building a career you genuinely enjoy. Our mission is simple:
              remove the hassle, cut through the noise, and help you find the best gigs faster—so you can spend less
              time searching and more time thriving.
            </Typography>
            <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
              Great freelance gigs are out there. Shoutt helps you find them.
            </Typography>

            <Typography variant={isMobileScreen ? 'h6' : 'h5'} mt={1} mb={isMobileScreen ? 0 : 1}>
              Our Founders
            </Typography>
            <Grid container>
              <Grid item xs={12} md={6} lg={5}>
                <Stack direction={'row'} spacing={2} mt={isMobileScreen ? 2 : undefined} alignItems={'center'}>
                  <Avatar
                    sx={{
                      width: isMobileScreen ? '90px' : '96px',
                      height: isMobileScreen ? '90px' : '96px',

                      mt: isMobileScreen ? 1.5 : undefined,
                      borderRadius: '12px',
                    }}
                    src={SaleemIm}
                    variant="square"
                  ></Avatar>
                  <Stack direction={'column'} pt={1}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color={'#101828'}
                      fontSize={isMobileScreen ? '20px' : undefined}
                    >
                      Saleem Yaqub
                    </Typography>
                    <Typography
                      variant={isMobileScreen ? 'body2' : 'body1'}
                      fontWeight={400}
                      color={'#475467'}
                      mt={0.5}
                    >
                      Co-Founder & CEO
                    </Typography>
                    <Link to="https://www.linkedin.com/in/saleemyaqub/" target="_blank">
                      <StyledIconButton sx={{ padding: 0 }}>
                        <LinkedInIcon
                          sx={{
                            height: isMobileScreen ? '24px' : '28px',
                            width: isMobileScreen ? '24px' : '28px',
                            mt: 0.5,
                            cursor: 'pointer',
                          }}
                        ></LinkedInIcon>
                      </StyledIconButton>
                    </Link>
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6} lg={5}>
                <Stack direction={'row'} spacing={2} mt={isMobileScreen ? 2 : undefined} alignItems={'center'}>
                  <Avatar
                    sx={{
                      width: isMobileScreen ? '90px' : '96px',
                      height: isMobileScreen ? '90px' : '96px',
                      mt: isMobileScreen ? 1.5 : undefined,
                      borderRadius: '12px',
                    }}
                    src={JonathanIm}
                    variant="square"
                  ></Avatar>
                  <Stack direction={'column'} pt={1}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color={'#101828'}
                      fontSize={isMobileScreen ? '20px' : undefined}
                    >
                      Jonathan Eadie
                    </Typography>
                    <Typography
                      variant={isMobileScreen ? 'body2' : 'body1'}
                      fontWeight={400}
                      color={'#475467'}
                      mt={0.5}
                    >
                      Co-Founder & CTO
                    </Typography>
                    <Link to="https://www.linkedin.com/in/jonathan-eadie/" target="_blank">
                      <StyledIconButton sx={{ padding: 0 }}>
                        <LinkedInIcon
                          sx={{
                            height: isMobileScreen ? '24px' : '28px',
                            width: isMobileScreen ? '24px' : '28px',
                            mt: 0.5,
                            cursor: 'pointer',
                          }}
                        ></LinkedInIcon>
                      </StyledIconButton>
                    </Link>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </DetailContainer>
        </Container>
      </Box>
    </>
  );
}
